/**
 * Packages & Services builder — services catalog section (list +
 * bootstrap defaults + create/edit). Extracted verbatim from the former
 * AgencyServicesPage monolith (Task 150-A). No behavior change: same
 * `bootstrapAgencyServices` call, same edit/create form wiring.
 */

import { useCallback, useEffect, useState } from 'react';
import { bootstrapAgencyServices } from '../../api/endpoints';
import type { AgencyServiceRow } from '../../api/types';
import { capabilityLabelAr } from '../../permissions';
import { EmptyState } from '../states';
import { billingIntervalLabel, handleApiErr } from './helpers';
import { isAgencyServiceVisible, isCommercialCapabilityVisible } from './launchVisibility';
import { ServiceForm } from './ServiceForm';

export function ServicesSection({
  services,
  canManage,
  onSaved,
}: {
  readonly services: ReadonlyArray<AgencyServiceRow>;
  readonly canManage: boolean;
  readonly onSaved: () => void;
}) {
  const [editing, setEditing] = useState<AgencyServiceRow | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const [autoBootstrapAttempted, setAutoBootstrapAttempted] = useState(false);
  const visibleServices = services.filter(isAgencyServiceVisible);

  const bootstrapDefaults = useCallback(async () => {
    setBootstrapError(null);
    setBootstrapping(true);
    try {
      await bootstrapAgencyServices();
      setBootstrapping(false);
      onSaved();
    } catch (err) {
      setBootstrapping(false);
      handleApiErr(err, setBootstrapError);
    }
  }, [onSaved]);

  useEffect(() => {
    if (!canManage || visibleServices.length > 0 || autoBootstrapAttempted || bootstrapping) return;
    setAutoBootstrapAttempted(true);
    void bootstrapDefaults();
  }, [autoBootstrapAttempted, bootstrapDefaults, bootstrapping, canManage, visibleServices.length]);

  return (
    <section className="card" style={{ padding: 22 }}>
      <div className="sec-title">
        <h2>الخدمات</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="meta">{visibleServices.length}</span>
          {canManage ? (
            <button
              type="button"
              className="btn-text"
              onClick={bootstrapDefaults}
              disabled={bootstrapping}
              style={{ padding: '6px 10px', fontSize: 12 }}
            >
              {bootstrapping ? <span className="spinner" /> : null}
              <span>
                {visibleServices.length === 0 ? 'تجهيز الخدمات الافتراضية' : 'استكمال الافتراضية'}
              </span>
            </button>
          ) : null}
        </div>
      </div>

      {bootstrapError ? (
        <div className="banner banner-error" style={{ marginBottom: 12 }}>
          {bootstrapError}
        </div>
      ) : null}

      {visibleServices.length === 0 ? (
        <div>
          <EmptyState
            title={bootstrapping ? 'جاري تجهيز الخدمات الافتراضية…' : 'لا توجد خدمات بعد'}
            description={
              bootstrapping
                ? 'سنضيف الخدمات الأساسية تلقائياً خلال لحظات.'
                : 'ابدأ بالخدمات الافتراضية للوكالة، ثم عدّلها أو ابنِ عليها باقاتك.'
            }
          />
          {canManage ? (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
              <button
                type="button"
                className="auth-cta"
                onClick={bootstrapDefaults}
                disabled={bootstrapping}
                style={{ width: 'auto' }}
              >
                {bootstrapping ? <span className="spinner" /> : null}
                <span>{bootstrapping ? 'جاري التجهيز…' : 'تجهيز الخدمات الافتراضية'}</span>
              </button>
            </div>
          ) : null}
        </div>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          {visibleServices.map((s) => (
            <li
              key={s.id}
              style={{
                padding: '12px 14px',
                border: '1px solid var(--line)',
                borderRadius: 'var(--r-md)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: 14 }}>{s.name}</strong>
                    <span className={`pill pill-${s.is_active ? 'active' : 'archived'}`}>
                      {s.is_active ? 'نشطة' : 'موقوفة'}
                    </span>
                  </div>
                  {s.description ? (
                    <p
                      className="tiny"
                      style={{ marginTop: 4, color: 'var(--muted)', whiteSpace: 'pre-wrap' }}
                    >
                      {s.description}
                    </p>
                  ) : null}
                  <div className="tiny" style={{ marginTop: 6, color: 'var(--muted)' }}>
                    {s.category ?? 'بدون فئة'} ·{' '}
                    {s.default_price_amount !== null
                      ? `${s.default_price_amount} ${s.default_price_currency}`
                      : 'بدون سعر'}{' '}
                    · {billingIntervalLabel(s.billing_interval)}
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <span
                      className="lbl"
                      style={{ display: 'block', marginBottom: 4, fontSize: 11 }}
                    >
                      ما الذي تفعّله هذه الخدمة داخل النظام
                    </span>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {s.capability_keys.length === 0 ? (
                        <span className="pill pill-info">
                          {capabilityLabelAr(s.system_capability_key)}
                        </span>
                      ) : (
                        s.capability_keys.filter(isCommercialCapabilityVisible).map((c) => (
                          <span
                            key={c}
                            className={
                              c === s.system_capability_key ? 'pill pill-active' : 'pill pill-info'
                            }
                          >
                            {capabilityLabelAr(c)}
                            {c === s.system_capability_key ? ' ★' : ''}
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                </div>
                {canManage ? (
                  <button
                    type="button"
                    className="btn-text"
                    onClick={() => setEditing(s)}
                    style={{ padding: '4px 8px', fontSize: 12 }}
                  >
                    تعديل
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      {canManage ? (
        <div style={{ marginTop: 14 }}>
          <button
            type="button"
            className="btn-text"
            onClick={() => setShowCreate(true)}
            style={{ padding: '8px 12px' }}
          >
            + إضافة خدمة
          </button>
        </div>
      ) : null}

      {editing ? (
        <ServiceForm
          mode="edit"
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onSaved();
          }}
        />
      ) : null}
      {showCreate ? (
        <ServiceForm
          mode="create"
          initial={null}
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false);
            onSaved();
          }}
        />
      ) : null}
    </section>
  );
}
